import React from 'react';
import { useAppSelector } from 'hooks';

import Icon, { faSpinner } from '~components/icon';
import { Currency } from '~components/form-fields/inputs';

import { ServiceTotal } from '~support-at-home/models';

import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';

interface ComponentProps {
  category_id: string;
  section?: any;
  /**
   * The service types with a changed row awaiting recalculation. A per-service line waits only for
   * its own service; the section's summary waits for any of them, because it sums them all.
   */
  pendingServiceTypeIds?: Set<string>;
}

/**
 * A figure that is known to be out of date, shown as a spinner rather than as a stale number.
 *
 * The point is that a number nobody has recalculated yet is worse than no number: it is indis-
 * tinguishable from a correct one, and a provider reading a total while a recalculation is still
 * in flight has no way to tell that the figure predates the change they just made.
 */
const PendingFigure: React.FC = () => (
  <span className="totals-pending" role="status" aria-label="Recalculating">
    <Icon icon={faSpinner} spin className="icon-small" />
  </span>
);

const BudgetSectionTotal: React.FC<ComponentProps> = ({ category_id, section, pendingServiceTypeIds }) => {
  const sectionPending = (pendingServiceTypeIds?.size ?? 0) > 0;
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);
  const { service_totals, totals } = section || {};

  const getServiceName = (service_type_id: string): string => {
    const service = serviceTypes.find(({ id }) => id === Number(service_type_id));
    return service ? service.name : service_type_id;
  };

  const buildRow = (serviceTotal: ServiceTotal, index: number) => {
    const { service_type_id, contribution, govSubsidy, total } = serviceTotal;
    const pending = !!pendingServiceTypeIds?.has(String(service_type_id));
    return (
      <div key={`row-${category_id}-${index}`} id={`row-${category_id}-${index}`} className="row mb-2 px-1">
        <div className="service-column">{getServiceName(service_type_id)}</div>
        <div className="contrib-column">
          {pending ? (
            <PendingFigure />
          ) : (
            <Currency
              id={`service-${service_type_id}-contribution`}
              name={`service-${service_type_id}-contribution`}
              type="currency"
              value={contribution}
              disabled
              noDecimals
            />
          )}
        </div>
        <div className="government-column">
          {pending ? (
            <PendingFigure />
          ) : (
            <Currency
              id={`service-${service_type_id}-govSubsidy`}
              name={`service-${service_type_id}-govSubsidy`}
              type="currency"
              value={govSubsidy}
              disabled
              noDecimals
            />
          )}
        </div>
        <div className="total-column">
          {pending ? (
            <PendingFigure />
          ) : (
            <Currency
              id={`service-${service_type_id}-total`}
              name={`service-${service_type_id}-total`}
              type="currency"
              value={total}
              disabled
              noDecimals
            />
          )}
        </div>
      </div>
    );
  };

  const { cost, contribution, govSubsidy } = totals || {};

  return (
    <>
      {/* Header row */}
      <div className="row fw-bold mb-2 border-bottom pb-2 px-1 totals-header">
        <div className="service-column">Service</div>
        <div className="contrib-column">Co-Contribution</div>
        <div className="government-column">Gov. Contribution</div>
        <div className="total-column">Total</div>
      </div>

      {service_totals &&
        service_totals.map((serviceTotal: ServiceTotal, index: number) => buildRow(serviceTotal, index))}

      <div className="row fw-bold border-top pt-2 px-1">
        <div className="service-column" />
        <div className="contrib-column">
          {sectionPending ? (
            <PendingFigure />
          ) : (
            <Currency
              id={`${category_id}-totalContribution`}
              name={`${category_id}-totalContribution`}
              type="currency"
              value={contribution}
              disabled
              noDecimals
            />
          )}
        </div>
        <div className="government-column">
          {sectionPending ? (
            <PendingFigure />
          ) : (
            <Currency
              id={`${category_id}-totalGovSubsidy`}
              name={`${category_id}-totalGovSubsidy`}
              type="currency"
              value={govSubsidy}
              disabled
              noDecimals
            />
          )}
        </div>
        <div className="total-column">
          {sectionPending ? (
            <PendingFigure />
          ) : (
            <Currency
              id={`${category_id}-totalCost`}
              name={`${category_id}-totalCost`}
              type="currency"
              value={cost}
              disabled
              noDecimals
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BudgetSectionTotal;
